import { Add, Check, ChevronLeft, Close, ExpandMore, HighlightOff, PlaylistAdd, Sync, Warning } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, IconButton, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useMemo, useState } from "react";
import { ViewCDGRJobTable } from "./ViewCDGRJobTable";
import { ViewCDGRJobChart } from "./ViewCDGRJobChart";
import { formatUwi } from "../helpers/formatUwi";

import { useDispatch, useSelector } from "react-redux";
import { addWells, getForecast, saveParams } from "../reducers/wellSlice";
import uuid from "react-uuid";
import { colors } from "../helpers/colors";
import { ViewCDGRJobChartOptions } from "./ViewCDGRJobChartOptions";
import { addList, selectLists } from "../reducers/listSlice";
import { selectOptions, setFluid, setFluids } from "../reducers/optionsSlice";
import { selectUser } from "../reducers/userSlice";
import { getGroupWellForecast, saveGroupWellParams } from "../reducers/groupWellSlice";
import { selectProjects } from "../reducers/projectSlice";
import { useSnackbar } from "notistack";
import { addCDGRJob, saveCDGRJob, selectCDGRJobs } from "../reducers/cdgrJobSlice";
import { convertVolume } from "../helpers/convertVolume";
import { formatNumber } from "../helpers/formatNumber";
import { getTypeCurveForecast, saveTypeCurve } from "../reducers/typeCurveSlice";

const KEY_DOWN = 40;
const KEY_UP = 38;
const KEY_Z = 90;
const KEY_X = 88;
const KEY_C = 67;


export const ViewCDGRJob = ({ setViewCDGRJobVisible, setManageJobsVisible }) => {
  
  const theme = useTheme();
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const options = useSelector(selectOptions);
  const user = useSelector(selectUser);
  const demoUser = user?.value?.subscription === 'Demo';
  const darkMode = user?.value?.darkMode;
  const projects = useSelector(selectProjects);
  const cdgrJobs = useSelector(selectCDGRJobs);
  const cdgrJob = cdgrJobs?.byId[cdgrJobs?.currentCDGRJobId];

  const [currentUwi, setCurrentUwi] = useState([...cdgrJob?.qcFlags]?.sort()?.[0] || [...cdgrJob?.wells?.map(well => well.uwi)]?.sort()?.[0]);
  const [currentTypeCurveName, setCurrentTypeCurveName] = useState(cdgrJob?.typeCurves?.[0]?.name);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentFluidTab, setCurrentFluidTab] = useState(0);
  const [showCheckedWells, setShowCheckedWells] = useState(false);

  const currentFluid = currentFluidTab === 0 ? 'gas' : 'condensate';
  const currentWell = 
    (currentTab === 0 || currentTab === 1 || currentTab === 4) ? cdgrJob?.wells?.find(w => w.uwi === currentUwi) : 
    currentTab === 2 ? cdgrJob?.typeCurves?.find(tc => tc.name === currentTypeCurveName) :
    currentTab === 3 ? (cdgrJob?.groupWell || cdgrJob?.previousGroupWell) :
    currentTab === 5 ? cdgrJob?.nonProducing?.find(w => w.uwi === currentUwi) : '';
  const currentTypeCurve = cdgrJob?.typeCurves?.find(tc => tc.id === currentWell?.typeCurveId);

  const lists = useSelector(selectLists);
  const list = lists?.byId[lists?.currentListId];

  const viewJobChartMemo = useMemo(() => {
    let currentWell;
    let typeCurve;

    if (currentTab === 2 && currentTypeCurveName) { // type curve tab
      currentWell = cdgrJob?.typeCurves?.find(tc => tc.name === currentTypeCurveName);
    } else if ((currentTab === 0 || currentTab === 1 || currentTab === 4) && currentUwi) {
      currentWell = cdgrJob?.wells?.find(w => w.uwi === currentUwi)
      typeCurve = cdgrJob?.typeCurves?.find(tc => tc.id === currentWell?.typeCurveId);
    } else if (currentTab === 5 && currentUwi) {
      currentWell = cdgrJob?.nonProducing?.find(w => w.uwi === currentUwi);
      typeCurve = null;
    };

    return (
      <>
        <ViewCDGRJobChartOptions darkMode={darkMode} currentTab={currentTab} list={list} />
        <ViewCDGRJobChart
          darkMode={darkMode}
          demoUser={demoUser}
          well={currentWell} 
          currentFluid={currentFluid} 
          showOutliers={options.showOutliers === 'Show'} 
          chartType={options.chartType} 
          chartTime={options.chartTime} 
          typeCurve={currentTab !== 2 ? typeCurve : cdgrJob?.typeCurves?.find(t => t.name === currentTypeCurveName)}
          groupWell={cdgrJob?.groupWell}
          previousGroupWell={cdgrJob?.previousGroupWell}
          currentTab={currentTab}
          options={options}
        />
      </>
    );

  }, [currentTab, currentTypeCurveName, currentUwi, darkMode, list, demoUser, currentFluid, options, cdgrJob?.typeCurves, cdgrJob?.groupWell, cdgrJob?.previousGroupWell, cdgrJob?.wells, cdgrJob?.nonProducing]);


  function handleKeyDown(event) {
    if (event.keyCode === KEY_DOWN) {
      if (currentTab === 0) { // qc flagged wells
        const uwis = [...cdgrJob?.qcFlags]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf < uwis.length - 1) {
          setCurrentUwi(uwis[indexOf + 1]);
        };
      } else if (currentTab === 1) { // all wells
        const uwis = [...cdgrJob?.wells?.map(w => w.uwi)]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf < uwis.length - 1) {
          setCurrentUwi(uwis[indexOf + 1]);
        };
      } else if (currentTab === 2) { // type curves
        const names = [...cdgrJob?.typeCurves?.map(tc => tc.name)]?.sort();
        const indexOf = names.indexOf(currentTypeCurveName);
        if (indexOf < names.length - 1) {
          setCurrentTypeCurveName(names[indexOf + 1]);
        };
      } else if (currentTab === 4) { // shut-ins
        const uwis = [...cdgrJob?.recentShutIns]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf < uwis.length - 1) {
          setCurrentUwi(uwis[indexOf + 1]);
        };
      } else if (currentTab === 5) { // non-producing
        const uwis = [...cdgrJob?.nonProducing?.map(w => w.uwi)]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf < uwis.length - 1) {
          setCurrentUwi(uwis[indexOf + 1]);
        };
      };
    } else if (event.keyCode === KEY_UP) {
      if (currentTab === 0) { // qc flagged wells
        const uwis = [...cdgrJob?.qcFlags]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf > 0) {
          setCurrentUwi(uwis[indexOf - 1]);
        };
      } else if (currentTab === 1) { // all wells
        const uwis = [...cdgrJob?.wells?.map(w => w.uwi)]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf > 0) {
          setCurrentUwi(uwis[indexOf - 1]);
        };
      } else if (currentTab === 2) { // type curves
        const names = [...cdgrJob?.typeCurves?.map(tc => tc.name)]?.sort();
        const indexOf = names.indexOf(currentTypeCurveName);
        if (indexOf > 0) {
          setCurrentTypeCurveName(names[indexOf - 1]);
        };
      } else if (currentTab === 4) { // shut-ins
        const uwis = [...cdgrJob?.recentShutIns]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf > 0) {
          setCurrentUwi(uwis[indexOf - 1]);
        };
      } else if (currentTab === 5) { // non-producing
        const uwis = [...cdgrJob?.nonProducing?.map(w => w.uwi)]?.sort();
        const indexOf = uwis.indexOf(currentUwi);
        if (indexOf > 0) {
          setCurrentUwi(uwis[indexOf - 1]);
        };
      };
    } else if (event.keyCode === KEY_Z && event.ctrlKey) { // reject fit
      if (currentTab === 0) {
        handleRejectForecast();
      } else if (currentTab === 2) {
        handleRejectTCForecast();
      };
    } else if (event.keyCode === KEY_X && event.ctrlKey) { // add to qc list
      if (currentTab === 0 || currentTab === 1) {
        handleAddWellToList();
      };
    } else if (event.keyCode === KEY_C && event.ctrlKey) { // accept fit
      if (currentTab === 0) {
        handleAcceptForecast();
      } else if (currentTab === 2) {
        handleAcceptTCForecast();
      };
    };
  };

  return (
    <Dialog 
      open={true} 
      onClose={() => setViewCDGRJobVisible(false)}
      fullWidth
      fullScreen
      maxWidth="xl"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        },
      }}
      elevation={0}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <IconButton onClick={() => setViewCDGRJobVisible(false)}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6">
              View Jobs
            </Typography>
          </div>
          <IconButton onClick={() => setViewCDGRJobVisible(false)}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        
        <div style={{display: 'flex', flexDirection: 'row', height: '100%', overflow: 'hidden'}}>
          <div style={{width: '50%', borderRight: `1px solid lightgrey`, height: '100%', overflow: 'scroll'}}>
            <Typography variant="h6" style={{marginBottom: 0}}>{cdgrJob?.name}</Typography>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <Typography variant="caption" style={{marginBottom: 0}}>Completed: {new Date(cdgrJob?.completed)?.toLocaleString()}</Typography>
              <Typography variant="caption" style={{marginBottom: 0}}>Well Error Threshold: {cdgrJob?.wellErrorThreshold}%</Typography>
              <Typography variant="caption" style={{marginBottom: 0}}>Type Curve Error Threshold: {cdgrJob?.typeCurveErrorThreshold}%</Typography>
              <Typography variant="caption" style={{marginBottom: 0}}>Group Well Name: {demoUser ? 'Demo Group Well' : cdgrJob?.previousGroupWell?.name}</Typography>
            </div>

            <Tabs
              value={currentTab}
              TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
              textColor={darkMode ? "inherit" : "primary"}
              onChange={(e, value) => {
                if (value === 1 && !showCheckedWells) {
                  setShowCheckedWells(true);
                };
                setCurrentTab(value);
              }}
              style={{
                backgroundColor: theme?.palette?.elevationOne?.light,
                borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
                marginTop: 40
              }}
              variant="scrollable"
            >
              <Tab label={`QC Wells (${cdgrJob?.qcFlags?.length || 0})`}  />
              <Tab label={`All Wells (${cdgrJob?.wells?.length || 0})`}  />
              <Tab label={`Type Curves (${cdgrJob?.typeCurves?.length || 0})`}  />
              <Tab label={`Group Well / Roll-up (${cdgrJob?.groupWell ? 1 : 0})`}  />
              <Tab label={`Recent Shut-Ins (${cdgrJob?.recentShutIns?.length || 0})`}  />
              <Tab label={`Non-producers (${cdgrJob?.nonProducing?.length || 0})`}  />
            </Tabs>

              {currentTab === 0 ? 
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginRight: 10}}>
                <Tooltip title="Add wells flagged for QC to Athena list">
                  <Button variant="contained" onClick={(e) => handleCopyWells(e, cdgrJob?.qcFlags)} size="small" startIcon={<PlaylistAdd />}>Add QC wells to Athena list</Button>
                </Tooltip>
                <FormGroup>
                  <FormControlLabel 
                    control={<Switch />} 
                    label={<Typography style={{fontSize: 14}}>Show Checked Wells</Typography>}
                    size="small" 
                    checked={showCheckedWells} 
                    onChange={() => setShowCheckedWells(prev => !prev)}
                  />
                </FormGroup>
              </div>
                :
                currentTab === 1 ?
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginRight: 10}}>
                <Tooltip title="Add all wells to Athena List">
                  <Button variant="contained" onClick={(e) => handleCopyWells(e, cdgrJob?.wells?.map(w => w.uwi))} size="small" startIcon={<PlaylistAdd />}>Add all wells to Athena List</Button>
                </Tooltip>
                <FormGroup>
                  <FormControlLabel 
                    control={<Switch />} 
                    label={<Typography style={{fontSize: 14}}>Show Checked Wells</Typography>}
                    size="small" 
                    checked={showCheckedWells} 
                    onChange={() => setShowCheckedWells(prev => !prev)}
                  />
                </FormGroup>
              </div>
                :
                currentTab === 2 ?
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginRight: 10}}>
                <Tooltip title="Add type curve to Athena">
                  <Button startIcon={<Add />} variant="contained" style={{marginRight: 20}} size="small" onClick={handleAddTypeCurve}>Add type curve to Athena</Button>
                </Tooltip>
              </div> 
                :
                currentTab === 3 ?
              <div style={{display: 'flex', alignItems: 'center', marginTop: 20, marginRight: 10}}>
                <Tooltip title="Recalculate group well">
                  <Button startIcon={<Sync />} variant="contained" onClick={handleRecalculateGroupWell} size="small">Recalculate group well</Button>
                </Tooltip>
                <Tooltip title="Add group well to Athena">
                  <Button startIcon={<Add />} variant="contained" onClick={handleAddGroupWell} size="small" style={{marginLeft: 20}}>Add group well to Athena</Button>
                </Tooltip>
              </div>
                :
              <></>
              }

            <div style={{marginRight: 20}}>
              <ViewCDGRJobTable 
                demoUser={demoUser}
                currentTab={currentTab} 
                currentUwi={currentUwi} 
                currentTypeCurveName={currentTypeCurveName} 
                setCurrentUwi={setCurrentUwi}
                setCurrentTypeCurveName={setCurrentTypeCurveName}
                showCheckedWells={showCheckedWells}
              />
            </div>

          </div>

          <div style={{width: '50%', paddingBottom: 10, height: '100%', overflow: 'scroll'}}>
            {(!currentUwi && (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5)) ? <></> :
            <>
              <div style={{width: '50%', position: 'fixed', zIndex: 2, backgroundColor: '#fff'}}>

                {currentWell && <div style={{marginLeft: 10}}>
                  <div style={{display: 'flex'}}>
                    <Tooltip title="Data is more than 2 weeks old!">
                      <span style={{display: 'flex', alignItems: 'center'}}>
                        <Warning 
                          color="secondary" 
                          style={{display: !currentWell.production.staleData && 'none'}} 
                        />
                      </span>
                    </Tooltip>
                    <Typography variant="h5">
                      {demoUser ? 'Demo' : (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? formatUwi(currentUwi) : 
                      currentTab === 2 ? currentTypeCurveName : 
                      cdgrJob?.groupWell?.name
                      }
                    </Typography>
                  </div>
                  <Typography variant="body1">
                    {demoUser ? 'Demo' : (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? `${currentWell?.completions?.['Well_Name']} | ${currentWell?.completions?.['License_number']} | ${currentWell?.completions?.['SBU']} | ${currentWell?.completions?.['SSD_Type_Curve'] || 'No Type Curve Found'}` :
                    currentTab === 2 ? 'Type Curve' : 'Group Well'}</Typography>
                </div>}

                <Tabs
                  value={currentFluidTab}
                  TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
                  textColor={darkMode ? "inherit" : "primary"}
                  onChange={(e, value) => handleFluidTabChange(value)}
                  style={{
                    backgroundColor: theme?.palette?.elevationOne?.light,
                    borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
                    marginBottom: 0
                  }}
                >
                  <Tab label='Gas' />
                  <Tab label='Condensate' />
                </Tabs>    

                {currentWell && currentTab === 0 ? <div style={{width: '100%', display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10}}>
                  <Tooltip title="Reject the new forecast and keep the previous forecast (shortcut Ctrl+Z)">
                    <span><Button 
                      startIcon={<HighlightOff />} 
                      variant="outlined" 
                      style={{marginRight: 20}} 
                      onClick={handleRejectForecast} 
                      size="small" 
                      color="secondary"
                      disabled={(!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                    >
                      Reject
                    </Button></span>
                  </Tooltip>
                  <Tooltip title="Reject and copy well to Athena list for forecast adjustment (shortcut Ctrl+X)">
                    <Button startIcon={<PlaylistAdd />} variant="outlined" style={{marginRight: 20}} onClick={handleAddWellToList} size="small">Add to Athena List</Button>
                  </Tooltip>
                  <Tooltip title="Accept the new forecast and update the well (shortcut Ctrl+C)">
                    <span><Button 
                      startIcon={<Check />} 
                      variant="contained" 
                      style={{marginRight: 20}} 
                      onClick={handleAcceptForecast} 
                      size="small"
                      disabled={(!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                    >
                      Accept
                    </Button></span>
                  </Tooltip>
                </div> :
                currentWell && currentTab === 1 ? <div style={{width: '100%', display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10}}>
                  <Tooltip title="Reject and copy well to Athena list for forecast adjustment (shortcut Ctrl+Q)">
                    <Button startIcon={<PlaylistAdd />} variant="outlined" style={{marginRight: 20}} onClick={handleAddWellToList} size="small">Add to Athena List</Button>
                  </Tooltip>
                </div> :
                currentTab === 2 ? 
                <div style={{width: '100%', display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10}}>
                  <Tooltip title="Reject the new forecast and keep the previous forecast (shortcut Ctrl+R)">
                    <span><Button 
                      startIcon={<HighlightOff />} 
                      variant="outlined" 
                      style={{marginRight: 20}}  
                      size="small" 
                      color="secondary" 
                      onClick={handleRejectTCForecast}
                      disabled={(!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                    >
                      Reject
                    </Button></span>
                  </Tooltip>
                  <Tooltip title="Accept the new forecast and update the type curve (shortcut Ctrl+A)">
                    <span><Button 
                      startIcon={<Check />} 
                      variant="contained" 
                      style={{marginRight: 20}} 
                      size="small" 
                      onClick={handleAcceptTCForecast}
                      disabled={(!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL))}
                    >
                      Accept
                    </Button></span>
                  </Tooltip>
                </div> :
                <></>
                }
                <Divider style={{width: '100%'}} />
              </div>

              <div style={{marginTop: (currentTab !== 3 && currentTab !== 4 && currentTab !== 5) ? 155 : 110, width: '100%'}}>
                <Accordion disableGutters elevation={0} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    {currentFluid === 'gas' ? 'Gas' : 'Condensate'} {(currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? 'Forecast' : currentTab === 2 ? 'Type Curve' : 'Group Well'} Summary
                  </AccordionSummary>

                  <AccordionDetails style={{padding: 0}}>
                    {!currentUwi ? <></> : (currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? 
                    <WellSummaryTable 
                      cdgrJob={cdgrJob}
                      currentWell={currentWell}
                      currentFluid={currentFluid}
                      currentTypeCurve={currentTypeCurve}
                    /> : 
                    currentTab === 2 ? 
                    <TypeCurveSummaryTable typeCurve={cdgrJob?.typeCurves?.find(tc => tc.name === currentTypeCurveName)} currentFluid={currentFluid} /> :
                    <GroupWellSummaryTable previousGroupWell={cdgrJob?.previousGroupWell} groupWell={cdgrJob?.groupWell} currentFluid={currentFluid} />}
                  </AccordionDetails>
                </Accordion>

                <Divider style={{width: '100%'}} />

                <Accordion disableGutters elevation={0} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    {(currentTab === 0 || currentTab === 1 || currentTab === 4 || currentTab === 5) ? 'Forecast' : currentTab === 2 ? 'Type Curve' : 'Group Well'} Chart
                  </AccordionSummary>

                  <AccordionDetails style={{padding: 0}}>
                    {viewJobChartMemo}
                  </AccordionDetails>
                </Accordion>

                <Divider style={{width: '100%'}} />
              </div>
            </>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // HELPERS

  function handleFluidTabChange(value) {
    try {
      setCurrentFluidTab(value);

      if (value === 0) {
        dispatch(setFluid({fluid: 'gas'}));
        dispatch(setFluids({fluids: ['gas', 'condensate']}));
      } else {
        dispatch(setFluid({fluid: 'condensate'}));
        dispatch(setFluids({fluids: ['condensate']}));
      };

    } catch (error) {
      console.log(error);
    };
  };

  function handleCopyWells(e, uwis) {
    e.stopPropagation();
    e.preventDefault();

    const listId = lists?.allIds?.find(id => lists.byId?.[id]?.name === `${cdgrJob?.name} - QC`);

    let list;

    if (!listId) {
      const id = uuid();
      const color = colors[Math.floor(Math.random() * colors.length - 1)];
      const name = `${cdgrJob?.name} - QC`;
  
      list = {
        id,
        name,
        description: `Created from evergreen job`,
        color,
        createdBy: user?.value?.email,
        lastModified: new Date(),
        wellUwis: uwis,
        typeCurveId: undefined,
        groupWellId: undefined,
        forecastListTab: 0,
        filters: {}
      };
      dispatch(addList({projectId: (projects?.currentProjectId || "default"), list}));
    } else {
      list = {...lists?.byId?.[listId]};
      list.wellUwis = uwis;
    };

    let wells = {};
    
    for (let uwi of uwis) {
      const color = colors[Math.floor(Math.random() * colors.length - 1)];

      const well = cdgrJob?.wells?.find(w => w.uwi === uwi);

      wells[uwi] = {
        uwi,
        formattedUwi: formatUwi(uwi),
        color,
        lastModified: new Date().toLocaleString(),
        status: 'processed',
        production: well?.production,
        fluidRatios: well?.fluidRatios,
        params: {gas: well?.job?.byFluid?.gas?.newParams, condensate: well?.job?.byFluid?.condensate?.newParams}
      };
    };

    dispatch(addWells({uwis: uwis, wells: wells, listId: list?.id}));
  
    for (let uwi of uwis) {
      const well = wells[uwi];
      dispatch(getForecast(well)); // Generate multi segment forecast for the well
    };

    setViewCDGRJobVisible(false);
    setManageJobsVisible(false);

  };

  function handleAddWellToList(e) {
    try {
      
      if (!lists.allIds.find(id => lists.byId?.[id]?.name === `${cdgrJob?.name} - QC`)) { // if QC does not exist, create it
        console.log('QC list not found, creating...');

        const id = uuid();
        const color = colors[Math.floor(Math.random() * colors.length - 1)];
        const name = `${cdgrJob?.name} - QC`;
        const list = {
          id,
          name,
          description: `Created from job ${cdgrJob?.name}`,
          color,
          createdBy: user?.value?.email,
          lastModified: new Date(),
          wellUwis: [ currentUwi ],
          typeCurveId: undefined,
          groupWellId: undefined,
          forecastListTab: 0,
          filters: {}
        };
        dispatch(addList({projectId: (projects?.currentProjectId || "default"), list}));
  
        const well = cdgrJob?.wells?.find(w => w.uwi === currentUwi);
  
        const wells = {
          [currentUwi]: {
            uwi: currentUwi,
            formattedUwi: formatUwi(currentUwi),
            color,
            lastModified: new Date().toLocaleString(),
            status: 'processed',
            production: well?.production,
            fluidRatios: well?.fluidRatios,
            params: {gas: well?.job?.byFluid?.gas?.newParams, condensate: well?.job?.byFluid?.condensate?.newParams}
          }
        };
       
    
        dispatch(addWells({uwis: [currentUwi], wells: wells, listId: id}));
        dispatch(getForecast(wells[currentUwi])); // Generate multi segment forecast for the well

      } else { // if QC list exists

        const listId = lists.allIds.find(id => lists.byId?.[id]?.name === `${cdgrJob?.name} - QC`);

        const color = colors[Math.floor(Math.random() * colors.length - 1)];

        const well = cdgrJob?.wells?.find(w => w.uwi === currentUwi);

        const wells = {
          [currentUwi]: {
            uwi: currentUwi,
            formattedUwi: formatUwi(currentUwi),
            color,
            lastModified: new Date().toLocaleString(),
            status: 'processed',
            production: well?.production,
            // forecast: {byFluid: {gas: well?.job?.byFluid?.gas?.newForecast, condensate: well?.job?.byFluid?.condensate?.newForecast}},
            params: {gas: well?.job?.byFluid?.gas?.newParams, condensate: well?.job?.byFluid?.condensate?.newParams}
          }
        };

        dispatch(addWells({
          uwis: [currentUwi], 
          wells, 
          listId: listId
        }));

        dispatch(getForecast(wells[currentUwi])); // Generate multi segment forecast for the well

      };

      let newCDGRJob = {...cdgrJob};

      let indexOf = cdgrJob.qcFlags?.indexOf(currentUwi);
      if (indexOf === -1 || indexOf === 0) {
        indexOf = cdgrJob.wells?.map(w => w.uwi)?.indexOf(currentUwi);
      };
      let newCurrentUwi = cdgrJob?.qcFlags?.length > 1 ? cdgrJob?.qcFlags?.[indexOf-1] : cdgrJob?.wells?.[indexOf-1]?.uwi;
      setCurrentUwi(newCurrentUwi);

      newCDGRJob.qcFlags = newCDGRJob?.qcFlags?.filter(f => f !== currentUwi);

      enqueueSnackbar(`Well ${currentUwi} added to list ${cdgrJob?.name} - QC`, {variant: 'success'});
      dispatch(addCDGRJob(newCDGRJob));


    } catch (error) {
      console.log(error);
    };
  };

  function handleRejectForecast(e) {
    try {
      let newCDGRJob = {...cdgrJob};
      const uwis = [...cdgrJob.qcFlags]?.sort();
      const indexOf = uwis?.indexOf(currentUwi);
      const newCurrentUwi = uwis?.[indexOf+1] || currentUwi;
      
      if (!newCDGRJob.checkedWells?.includes(currentUwi)) {
        newCDGRJob.checkedWells = [...newCDGRJob?.checkedWells, currentUwi];
      };

      setCurrentUwi(newCurrentUwi);

      // newCDGRJob.qcFlags = newCDGRJob?.qcFlags?.filter(f => f !== currentUwi);
      dispatch(addCDGRJob(newCDGRJob));

      newCDGRJob = {
        id: newCDGRJob?.id,
        name: newCDGRJob?.name,
        description: newCDGRJob?.description,
        frequency: newCDGRJob?.frequency,
        completed: newCDGRJob?.completed,
        qcFlags: newCDGRJob?.qcFlags,
        checkedWells: newCDGRJob?.checkedWells,
        totalWells: newCDGRJob?.totalWells,
        wells: newCDGRJob?.wells?.map(w => w.uwi),
        recentShutIns: newCDGRJob?.recentShutIns,
        nonProducing: newCDGRJob?.nonProducing?.map(w => w.uwi),
        location: newCDGRJob?.location,
        typeCurves: newCDGRJob?.typeCurves?.map(t => t.name),
        tcQCFlags: newCDGRJob?.tcQCFlags,
        groupWell: newCDGRJob?.groupWell?.name,
        wellErrorThreshold: newCDGRJob?.wellErrorThreshold,
        typeCurveErrorThreshold: newCDGRJob?.typeCurveErrorThreshold
      };

      dispatch(saveCDGRJob(newCDGRJob)).then(res => {
        console.log('CDGR job saved successfully');
      });

    } catch (error) {
      console.log(error);
    };
  };

  function handleAcceptForecast(e) {
    try {
      if (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL)) return;

      let newCDGRJob = {...cdgrJob};

      const uwis = [...cdgrJob.qcFlags]?.sort();
      const indexOf = uwis?.indexOf(currentUwi);
      const newCurrentUwi = uwis?.[indexOf+1] || currentUwi;
      
      if (!newCDGRJob.checkedWells?.includes(currentUwi)) {
        newCDGRJob.checkedWells = [...newCDGRJob?.checkedWells, currentUwi];
      };

      setCurrentUwi(newCurrentUwi);

      dispatch(addCDGRJob(newCDGRJob));


      const well = cdgrJob?.wells?.find(w => w.uwi === currentUwi);
      const color = colors[Math.floor(Math.random() * colors.length)];

      let newParams = {};

      Object.keys(well?.job?.byFluid)?.forEach(fluid => {
        newParams[fluid] = well?.job?.byFluid?.[fluid]?.newParams;
      });

      const newWell = {
        uwi: well?.uwi,
        createdBy: well?.createdBy,
        color,
        lastModified: new Date()?.toISOString(),
        details: well?.details,
        params: newParams
      };


      dispatch(saveParams({well: newWell, email: user?.value?.email})).then(res => {
        if (res?.payload?.success) {
          enqueueSnackbar(`Well ${well.uwi} saved successfully`, {variant: 'success'});
        };
      });

      newCDGRJob = {
        id: newCDGRJob?.id,
        name: newCDGRJob?.name,
        description: newCDGRJob?.description,
        frequency: newCDGRJob?.frequency,
        completed: newCDGRJob?.completed,
        qcFlags: newCDGRJob?.qcFlags,
        checkedWells: newCDGRJob?.checkedWells,
        totalWells: newCDGRJob?.totalWells,
        wells: newCDGRJob?.wells?.map(w => w.uwi),
        recentShutIns: newCDGRJob?.recentShutIns,
        nonProducing: newCDGRJob?.nonProducing?.map(w => w.uwi),
        location: newCDGRJob?.location,
        typeCurves: newCDGRJob?.typeCurves?.map(t => t.name),
        tcQCFlags: newCDGRJob?.tcQCFlags,
        groupWell: newCDGRJob?.groupWell?.name,
        wellErrorThreshold: newCDGRJob?.wellErrorThreshold,
        typeCurveErrorThreshold: newCDGRJob?.typeCurveErrorThreshold
      };

      dispatch(saveCDGRJob(newCDGRJob)).then(res => {
        console.log('CDGR job saved successfully');
      });

    } catch (error) {
      console.log(error);
    };
  };

  function handleAddTypeCurve() {
    try {
      let newTypeCurve = {
        ...currentWell, 
        id: uuid(), 
        name: demoUser ? 'Demo Type Curve' : `${currentWell?.name}-Copy`, 
        createdBy: user?.value?.email, 
        production: currentWell?.production?.newProduction || {}
      };

      if (!lists?.allIds?.find(id => lists?.byId?.[id]?.name === `${cdgrJob?.name} - QC`)) {
        const id = uuid();
        const color = colors[Math.floor(Math.random() * colors.length - 1)];
        const name = `${cdgrJob?.name} - QC`;
        const list = {
          id,
          name,
          description: `Created from evergreen job`,
          color,
          createdBy: user.value.email,
          lastModified: new Date(),
          wellUwis: [],
          typeCurveId: newTypeCurve?.id,
          groupWellId: undefined,
          forecastListTab: 0,
          filters: {}
        };
        dispatch(addList({projectId: (projects?.currentProjectId || "default"), list}));
      };

      delete newTypeCurve["_id"];

      dispatch(getTypeCurveForecast(newTypeCurve)).then(() => {
        enqueueSnackbar(`Type curve ${demoUser ? 'Demo Type Curve' : currentWell?.name} added successfully`, {variant: 'success'});
      });
        // .then(res => {
        //   console.log(res.payload);
        //   dispatch(saveTypeCurve(newTypeCurve))
        //   .then(save => {
        //   })
        // });

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleAddGroupWell() {
    try {
      
      const newGroupWell = {...cdgrJob?.groupWell, id: uuid(), name: demoUser ? 'Demo Group Well' : `${cdgrJob?.groupWell?.name}-Copy`, createdBy: user?.value?.email};
      dispatch(getGroupWellForecast(newGroupWell))
      .then(res => {
        const groupWell = res.payload;
        dispatch(saveGroupWellParams(groupWell))
        .then(save => {
          enqueueSnackbar(`Group well ${demoUser ? 'Demo Type Curve' : cdgrJob?.groupWell?.name} added successfully`, {variant: 'success'});
        })
      });
      
      if (!lists?.allIds?.find(id => lists?.byId?.[id]?.name === `${cdgrJob?.name} - QC`)) {
        const id = uuid();
        const color = colors[Math.floor(Math.random() * colors.length - 1)];
        const name = `${cdgrJob?.name} - QC`;
        const list = {
          id,
          name,
          description: `Created from evergreen job`,
          color,
          createdBy: user.value.email,
          lastModified: new Date(),
          wellUwis: [],
          typeCurveId: undefined,
          groupWellId: newGroupWell?.id,
          forecastListTab: 0,
          filters: {}
        };
        dispatch(addList({projectId: (projects?.currentProjectId || "default"), list}));
      };

      // setViewCDGRJobVisible(false);
      // setManageJobsVisible(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleRecalculateGroupWell() {
    try {
      
      console.log('Recalculating group well');

      // setViewCDGRJobVisible(false);
      // setManageJobsVisible(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleRejectTCForecast() {
    try {

      console.log('Rejecting type curve forecast');
      let newCDGRJob = {...cdgrJob};
      let indexOf = cdgrJob?.typeCurves?.map(tc => tc.name)?.indexOf(currentTypeCurveName);
      const currentName = currentTypeCurveName;
      let newCurrentTypeCurveName = cdgrJob?.typeCurves?.length > 1 ? cdgrJob?.typeCurves?.[indexOf-1] : '';
      setCurrentTypeCurveName(newCurrentTypeCurveName);

      newCDGRJob.typeCurves = newCDGRJob?.typeCurves?.filter(tc => tc?.name !== currentName);
      dispatch(addCDGRJob(newCDGRJob));
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleAcceptTCForecast() {
    try {
      if (!user?.value?.email?.includes('ssdynamics.com') && !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL)) return;

      console.log('Accepting type curve forecast');

      let newCDGRJob = {...cdgrJob};
      let indexOf = cdgrJob?.typeCurves?.map(tc => tc.name)?.indexOf(currentTypeCurveName);
      const currentName = currentTypeCurveName;
      let newCurrentTypeCurveName = cdgrJob?.typeCurves?.length > 1 ? cdgrJob?.typeCurves?.[indexOf-1] : '';
      setCurrentTypeCurveName(newCurrentTypeCurveName);

      newCDGRJob.typeCurves = newCDGRJob?.typeCurves?.filter(tc => tc?.name !== currentName);
      dispatch(addCDGRJob(newCDGRJob));

      let newTypeCurve = {...cdgrJob?.typeCurves?.find(tc => tc.name === currentName)};

      let newTypeCurveParams = {};
      
      for (let fluid of Object.keys(newTypeCurve?.job?.byFluid)) {

        let fluidParams = {...newTypeCurve.job.byFluid[fluid]?.newParams};
        fluidParams.eur = {
          cutoff: newTypeCurve?.job?.byFluid[fluid]?.newEur?.p50?.volume,
          value: newTypeCurve?.job?.byFluid[fluid]?.newEur?.p50?.cumVolume
        };

        newTypeCurveParams[fluid] = fluidParams;
      };

      newTypeCurve = {
        id: newTypeCurve.id,
        color: newTypeCurve.color,
        createdBy: newTypeCurve.createdBy,
        description: newTypeCurve.description,
        details: newTypeCurve.details,
        fullCycle: newTypeCurve.fullCycle,
        lastModified: newTypeCurve.lastModified,
        name: newTypeCurve.name,
        normalization: newTypeCurve.normalization,
        normalizationPeakRate: newTypeCurve.normalizationPeakRate,
        params: newTypeCurveParams,
        production: newTypeCurve.production,
        public: newTypeCurve.public,
        uwis: newTypeCurve.uwis,
        listIds: newTypeCurve.listIds,
        location: newTypeCurve.location
      };

      dispatch(saveTypeCurve(newTypeCurve)).then(res => {
        if (res?.payload?.success) {
          enqueueSnackbar(`Type curve ${newTypeCurve.name} saved successfully`, {variant: 'success'});
        };
      });


    } catch (error) {
      console.log(error);
      return;
    };
  };
};

function WellSummaryTable({currentWell, currentFluid, currentTypeCurve}) {
  const prevParams = currentWell?.job?.byFluid?.[currentFluid]?.previousParams;
  const newParams = currentWell?.job?.byFluid?.[currentFluid]?.newParams;
  const prevEur = currentWell?.job?.byFluid?.[currentFluid]?.prevEur;
  const newEur = currentWell?.job?.byFluid?.[currentFluid]?.newEur;
  // const prevEurCutoff = currentWell?.job?.byFluid?.[currentFluid]?.prevEurCutoff;
  // const newEurCutoff = currentWell?.job?.byFluid?.[currentFluid]?.newEurCutoff;
  const prevRemaining = currentWell?.job?.byFluid?.[currentFluid]?.prevRemaining;
  const newRemaining = currentWell?.job?.byFluid?.[currentFluid]?.newRemaining;
  // const prevEur = currentWell?.previousEur?.byFluid?.[currentFluid];
  // const newEur = currentWell?.newEur?.byFluid?.[currentFluid];
  // const prevForecast = currentWell?.job?.byFluid?.[currentFluid]?.previousForecast;
  // const newForecast = currentWell?.job?.byFluid?.[currentFluid]?.newForecast;
  const tcParams = currentTypeCurve?.params?.[currentFluid];
  const tcEur = currentTypeCurve ? currentTypeCurve?.production?.previousProduction?.byFluid?.[currentFluid]?.slice(-1)?.[0] : 0;

  return (
    <TableContainer style={{marginBottom: 10, padding: 0}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}><b>Parameter</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>Previous</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>New</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>Type Curve</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '40%'}}>
              Start Month
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevParams?.ti}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newParams?.ti}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.ti}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '40%'}}>
              Initial Rate ({currentFluid === 'gas' ? 'mscf' : 'stb'}/d)
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevParams?.q1i > 0.1 ? formatNumber(convertVolume(prevParams?.q1i, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newParams?.q1i > 0.1 ? formatNumber(convertVolume(newParams?.q1i, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.q1i > 0.1 ? formatNumber(convertVolume((tcParams?.q1i || 0), 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>Initial Decline Rate (%)</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevParams?.di > 0 ? (prevParams?.di * 100)?.toFixed(2) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newParams?.di > 0 ? (newParams?.di * 100)?.toFixed(2) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.di > 0 ? (tcParams?.di * 100)?.toFixed(2) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>Hyperbolic Constant</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{!isNaN(prevParams?.b1) ? prevParams?.b1?.toFixed(2) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{!isNaN(newParams?.b1) ? newParams?.b1?.toFixed(2) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{!isNaN(tcParams?.b1) ? tcParams?.b1?.toFixed(2) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>EUR P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell>
            {/* <TableCell align="left" style={{width: '40%'}}>EUR P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'} @ {convertVolume(prevEurCutoff?.p90)?.toFixed(2)} {currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell> */}
            <TableCell align="right" style={{width: '20%'}}>{prevEur?.p90 > 0.1 ? formatNumber(convertVolume(prevEur?.p90, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newEur?.p90 > 0.1 ? formatNumber(convertVolume(newEur?.p90, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcEur?.cumVolume > 0.1 ? formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.p90?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.p90?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell> */}
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>EUR P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell>
            {/* <TableCell align="left" style={{width: '40%'}}>EUR P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'} @ {convertVolume(prevEurCutoff?.p50)?.toFixed(2)} {currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell> */}
          <TableCell align="right" style={{width: '20%'}}>{prevEur?.p50 > 0.1 ? formatNumber(convertVolume(prevEur?.p50, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{newEur?.p50 > 0.1 ? formatNumber(convertVolume(newEur?.p50, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{tcEur?.cumVolume > 0.1 ? formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.p50?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.p50?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell> */}
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>EUR P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell>
            {/* <TableCell align="left" style={{width: '40%'}}>EUR P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'} @ {convertVolume(prevEurCutoff?.p10)?.toFixed(2)} {currentFluid === 'gas' ? 'mscf' : 'stb'}/d)</TableCell> */}
          <TableCell align="right" style={{width: '20%'}}>{prevEur?.p10 > 0.1 ? formatNumber(convertVolume(prevEur?.p10, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{newEur?.p10 > 0.1 ? formatNumber(convertVolume(newEur?.p10, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{tcEur?.cumVolume > 0.1 ? formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.p10?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.p10?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(tcEur?.cumVolume, 'field', currentFluid)?.toFixed(1))}</TableCell> */}
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>Remaining P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{prevRemaining?.p90 > 0.1 ? formatNumber(convertVolume(prevRemaining?.p90, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{newRemaining?.p90 > 0.1 ? formatNumber(convertVolume(newRemaining?.p90, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{'-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(prevEur?.p90?.cumVolume, 'field', currentFluid) - prevForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p90)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(newEur?.p90?.cumVolume, 'field', currentFluid) - newForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p90)?.toFixed(1))}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{0}</TableCell> */}
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>Remaining P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{prevRemaining?.p50 > 0.1 ? formatNumber(convertVolume(prevRemaining?.p50, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{newRemaining?.p50 > 0.1 ? formatNumber(convertVolume(newRemaining?.p50, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{'-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(prevEur?.p50?.cumVolume, 'field', currentFluid) - prevForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p50)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(newEur?.p50?.cumVolume, 'field', currentFluid) - newForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p50)?.toFixed(1))}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{0}</TableCell> */}
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '40%'}}>Remaining P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{prevRemaining?.p10 > 0.1 ? formatNumber(convertVolume(prevRemaining?.p10, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{newRemaining?.p50 > 0.1 ? formatNumber(convertVolume(newRemaining?.p10, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          <TableCell align="right" style={{width: '20%'}}>{'-'}</TableCell>
            {/* <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(prevEur?.p10?.cumVolume, 'field', currentFluid) - prevForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p10)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber((convertVolume(newEur?.p10?.cumVolume, 'field', currentFluid) - newForecast?.filter(f => new Date(f.date)?.getTime() > new Date()?.getTime())?.[0]?.cumVolume?.p10)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{0}</TableCell> */}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function TypeCurveSummaryTable({typeCurve, currentFluid}) {
  const prevEur = typeCurve?.job?.byFluid?.[currentFluid]?.previousEur;
  const newEur = typeCurve?.job?.byFluid?.[currentFluid]?.newEur;
  const tcParams = typeCurve?.job?.byFluid?.[currentFluid]?.previousParams;
  const newTCParams = typeCurve?.job?.byFluid?.[currentFluid]?.newParams;
  
  // console.log(typeCurve)

  return (
    <TableContainer style={{marginBottom: 10, padding: 0}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}><b>Parameter</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>Previous</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>New</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '60%'}}>
              Start Month
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.ti > 0 ? tcParams?.ti : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newTCParams?.ti > 0 ? newTCParams?.ti : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '60%'}}>
              Initial Rate ({currentFluid === 'gas' ? 'mscf' : 'stb'}/d)
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.q1i > 0.1 ? formatNumber(convertVolume(tcParams?.q1i, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newTCParams?.q1i > 0.1 ? formatNumber(convertVolume(newTCParams?.q1i, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>Initial Decline Rate (%)</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.di > 0 ? (tcParams?.di * 100 || 0)?.toFixed(2) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newTCParams?.di > 0 ? (newTCParams?.di * 100 || 0)?.toFixed(2) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>Hyperbolic Constant</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{tcParams?.b1?.toFixed(2) || '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newTCParams?.b1?.toFixed(2) || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '60%'}}>
              Wells
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{typeCurve?.uwis?.length}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{typeCurve?.uwis?.length}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevEur?.p90?.cumVolume > 0.1 ? formatNumber(convertVolume(prevEur?.p90?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newEur?.p90?.cumVolume > 0.1 ? formatNumber(convertVolume(newEur?.p90?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevEur?.p50?.cumVolume > 0.1 ? formatNumber(convertVolume(prevEur?.p50?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newEur?.p50?.cumVolume > 0.1 ? formatNumber(convertVolume(newEur?.p50?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{prevEur?.p10?.cumVolume > 0.1 ? formatNumber(convertVolume(prevEur?.p10?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{newEur?.p10?.cumVolume > 0.1 ? formatNumber(convertVolume(newEur?.p10?.cumVolume, 'field', currentFluid)?.toFixed(1)) : '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function GroupWellSummaryTable({previousGroupWell, groupWell, currentFluid}) {
  const prevEur = previousGroupWell?.forecast?.byFluid?.[currentFluid]?.slice(-1)?.[0];
  const newEur = groupWell?.forecast?.byFluid?.[currentFluid]?.slice(-1)?.[0];
  
  return (
    <TableContainer style={{marginBottom: 10, padding: 0}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}><b>Parameter</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>Previous</b></TableCell>
            <TableCell align="right" style={{width: '20%'}}><b>New</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row" style={{width: '60%'}}>
              Wells
            </TableCell>
            <TableCell align="right" style={{width: '20%'}}>{groupWell?.uwis?.length}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{groupWell?.uwis?.length}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p90, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p90, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p50, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p50, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>EUR P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p10, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p10, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>Remaining P90 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p90 - previousGroupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p90, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p90 - groupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p90, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>Remaining P50 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p50 - previousGroupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p50, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p50 - groupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p50, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="left" style={{width: '60%'}}>Remaining P10 ({currentFluid === 'gas' ? 'mscf' : 'stb'})</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(prevEur?.cumVolume?.p10 - previousGroupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p10, 'field', currentFluid)?.toFixed(1))}</TableCell>
            <TableCell align="right" style={{width: '20%'}}>{formatNumber(convertVolume(newEur?.cumVolume?.p10 - groupWell?.forecast?.byFluid?.[currentFluid]?.filter(f => new Date(f.date).getTime() >= new Date().getTime())?.[0]?.cumVolume?.p10, 'field', currentFluid)?.toFixed(1))}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
